<template>
  <div id="app">
    <header class="hero">
      <div class="hero-body">
        <div class="container">
          <h1 class="title">
            <span class="cuneiform">𒆳𒉡𒄄𒀀</span> KUR.NU.GI<sub>4</sub>.A
          </h1>
          <h2 class="subtitle">
            {{ $t("subtitle") }}
          </h2>
        </div>
        <div class="container">
          <a href="/ja">Japanese</a> / <a href="/en">English</a>
        </div>
      </div>
    </header>
    <section class="section">
      <div class="container">
        <div class="columns  is-centered ">
          <div class="font-selector column">
            <h2>{{ $t("config.type") }}</h2>
            <b-field position="is-centered">
              <b-radio
                v-model="cuneiformClass"
                name="font"
                native-value="sumerian"
                size="is-small"
              >
                {{ $t("config.sumerian") }}
              </b-radio>
              <b-radio
                v-model="cuneiformClass"
                name="font"
                native-value="akkadian"
                size="is-small"
              >
                {{ $t("config.akkadian") }}
              </b-radio>
            </b-field>
          </div>
          <div class="level-selector column">
            <h2>{{ $t("config.glossary") }}</h2>
            <b-field>
              <b-slider
                :min="0"
                :max="3"
                v-model="level"
                :tooltip="true"
                :custom-formatter="levelInfo"
              >
                <b-slider-tick :value="0">Tiny</b-slider-tick>
                <b-slider-tick :value="1">Basic</b-slider-tick>
                <b-slider-tick :value="2">Advance</b-slider-tick>
                <b-slider-tick :value="3">Expert</b-slider-tick>
              </b-slider>
            </b-field>
          </div>
          <div class="transliteral-selector column">
            <h2>{{ $t("config.transliteral") }}</h2>
            <b-radio
              v-model="transliteralStyle"
              name="transliteral"
              native-value="kananize"
              size="is-small"
              v-if="$i18n.locale === 'ja'"
            >
              あああ
            </b-radio>
            <b-radio
              v-model="transliteralStyle"
              name="transliteral"
              native-value="traditional"
              size="is-small"
            >
              a á à
            </b-radio>
            <b-radio
              v-model="transliteralStyle"
              name="transliteral"
              native-value="modern"
              size="is-small"
            >
              a a₂ a₃
            </b-radio>
          </div>
        </div>
        <div class="columns">
          <div class="column filler" />
          <div class="column is-8 is-centered">
            <b-field :label="$t('config.input')" label-position="on-border">
              <b-input
                class="cuneiform"
                :custom-class="cuneiformClass"
                expanded
                v-model="text"
              />
            </b-field>
          </div>
          <div class="column filler" />
        </div>
        <div class="columns">
          <div class="column filler" />
          <div class="column is-8 is-centered">
            <b-field :label="$t('config.output')" label-position="on-border">
              <b-input
                class="transliteral"
                expanded
                v-model="transliteralView"
                readonly
              />
            </b-field>
          </div>
          <div class="column filler" />
        </div>
      </div>
    </section>

    <section class="section columns">
      <div class="container column is-12-desktop">
        <b-tabs type="is-boxed">
          <b-tab-item :label="$t('tabs.signlist')">
            <signs-order-table
              :level="level"
              @select="putSign"
              :cuneiform-class="cuneiformClass"
              :transliteral-style="transliteralStyle"
            />
            <!-- <PartsSelector @select="putSign" /> -->
          </b-tab-item>
          <b-tab-item :label="$t('tabs.syllabic')">
            <syllables-table
              :level="level"
              @select="putSign"
              :cuneiform-class="cuneiformClass"
              :transliteral-style="transliteralStyle"
            />
          </b-tab-item>
          <b-tab-item :label="$t('tabs.analyze')">
            <analysis-view
              :level="level"
              :text="text"
              :cuneiform-class="cuneiformClass"
              :transliteral-style="transliteralStyle"
              @change-result="changeResult"
            />
          </b-tab-item>
        </b-tabs>
      </div>
    </section>
    <footer class="footer">
      <div class="content">
        <p>developped by <a href="https://twitter.com/uyum">uyum</a></p>
        <p>
          Source of Cuneiform Sign List :
          <a href="http://home.zcu.cz/~ksaskova/">CUNEIFORM SIGN LIST </a>
        </p>
      </div>
    </footer>
  </div>
</template>

<script>
import Vue from "vue";
import SignsOrderTable from "./components/SignsOrderTable.vue";
import SyllablesTable from "./components/SyllablesTable.vue";
import AnalysisView from "./components/AnalysysView.vue";
import Kananize from "./modules/Kananize.js";
import DisplayTransLiteral from "./modules/Transliteral.js";

export default Vue.extend({
  name: "app",
  components: {
    SignsOrderTable,
    SyllablesTable,
    AnalysisView,
  },

  data() {
    return {
      text: "",
      result: [],
      cuneiformClass: "akkadian",
      transliteralStyle: "kananize",
      level: 0,
    };
  },
  methods: {
    putSign(sign) {
      this.text = this.text + sign.sign;
    },
    changeResult(result) {
      this.result = result;
    },
    levelInfo(level) {
      switch (level) {
        case 0:
          return this.$t("config.infoTiny");
        case 1:
          return this.$t("config.infoBasic");
        case 2:
          return this.$t("config.infoAdvance");
        case 3:
          return this.$t("config.infoExpert");
      }
    },
  },
  computed: {
    transliteralView() {
      var str = "";
      this.result.forEach((sign) => {
        switch (this.transliteralStyle) {
          case "kananize":
            str += Kananize(sign.value, true);
            break;
          default:
            if (str === "")
              str = DisplayTransLiteral(sign.value, this.transliteralStyle);
            else
              str +=
                "-" + DisplayTransLiteral(sign.value, this.transliteralStyle);
            break;
        }
      });
      return str;
    },
  },

  created() {
    switch (window.location.pathname) {
      case "/ja":
        this.$i18n.locale = "ja";
        this.level = 0;
        this.transliteralStyle = "kananize";
        break;
      default:
      case "/en":
        this.$i18n.locale = "en";
        this.level = 1;
        this.transliteralStyle = "traditional";
        break;
    }
  },
  mounted() {
    this.$ga.page(window.location.pathname);
  },
});
</script>

<style lang="scss">
@import url(//fonts.googleapis.com/earlyaccess/notosanscuneiform.css);

#app {
  font-family: "Roboto Slab", "Noto Serif JP", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}
h1.title {
  color: dimgray;
}
h2.subtitle {
  font-family: "Roboto Slab", "Noto Serif JP", serif;
  font-weight: 100;
  font-size: 1rem;
}

div.filler {
  padding: 0px;
}

.section {
  padding: 1.5rem 1.5rem;
}

.akkadian {
  font-family: sinacherib, sans-serif;
}

.sumerian {
  font-family: "Noto Sans Cuneiform", sans-serif;
}

.transliteral .input {
  font-family: Helvetica, Arial, sans-serif;
}

.tabs ul {
  width: 100%;
  flex-direction: row;
  flex-wrap: wrap;
}
.tabs li {
  width: 33%;
  min-width: 100px;
}
</style>
